import React, { useState } from 'react';
import { authAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import { Button } from './ui/Button';
import { IconButton } from './ui';
import { unwrapResponse } from '../utils/unwrapResponse';

interface EmailVerificationBannerProps {
  email: string;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);

  const handleResend = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await authAPI.resendVerificationEmail();
      setMessage(unwrapResponse<any>(response.data).message || 'Verification email sent! Please check your inbox.');
    } catch (error: unknown) {
      setMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (dismissed) {
    return null;
  }

  return (
    <div className="bg-warning/10 border-b-2 border-warning py-3 px-4 md:py-4 md:px-6" role="alert" aria-live="polite">
      {/* Desktop: full layout */}
      <div className="hidden md:flex max-w-[1400px] mx-auto items-center gap-4 flex-wrap">
        <div className="text-2xl shrink-0">{'⚠️'}</div>
        <div className="flex-1 min-w-[200px] flex flex-col gap-1">
          <strong className="text-warning font-bold text-base">Email not verified</strong>
          <span className="text-warning text-sm opacity-90">
            Please verify your email address ({email}) to access all features.
          </span>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleResend}
            disabled={loading}
            className="border-warning text-warning hover:bg-warning/10"
          >
            {loading ? 'Sending...' : 'Resend Email'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="text-warning"
          >
            Dismiss
          </Button>
        </div>
      </div>
      {/* Mobile: compact single-line */}
      <div className="flex md:hidden items-center gap-2 flex-nowrap max-w-[1400px] mx-auto">
        <span className="flex-1 text-sm text-warning whitespace-nowrap overflow-hidden text-ellipsis">
          {'⚠️'} Verify your email to access all features.
        </span>
        <div className="shrink-0 flex gap-1 items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleResend}
            disabled={loading}
            className="border-warning text-warning text-xs py-1 px-2 border-[1.5px]"
          >
            {loading ? '...' : 'Resend'}
          </Button>
          <IconButton
            icon={<>{'✕'}</>}
            aria-label="Dismiss verification banner"
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
          />
        </div>
      </div>
      {message && (
        <div className={`w-full mt-2 py-2 px-3 rounded-lg text-sm ${
          message.includes('Failed')
            ? 'bg-danger/15 text-danger border border-danger'
            : 'bg-success/15 text-success border border-success'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default EmailVerificationBanner;
